import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { RenderingErrorBoundaryChildProps } from 'components/RenderingErrorBoundary';
import AIButton from 'components/shared/ai/AIButton';
import Explainer from 'components/shared/ai/Explainer';
import SourceChip from 'components/shared/ai/SourceChip';
import Tooltip from 'components/shared/Tooltip';
import { Flags } from 'constants/flags';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { useTrackAIJob } from 'hooks/useTrackAIJob';
import { SummarizableReview, updateReviewCacheWithAIJob } from 'lib/aiJobHelpers';
import { simpleDate } from 'lib/date';
import AIJob, { AIJobOptions } from 'models/AIJob';
import { PERMISSIONS } from 'models/Profile';
import { ReviewType } from 'models/Review';
import Referral from 'models/reviews/Referral';
import { createAIJob } from 'services/api/aiJobs';
import { authorizationQueryKeys, referralQueryKeys } from 'services/api/reviews';
import { AIJobNotificationType } from 'stores/aiJobNotificationStore';
import { colors } from 'styles/theme/colors';
import ErrorWarningIcon from 'svg/ErrorWarningIcon';

import { DownloadAIExportButton } from '../DownloadAIExportButton';

import { getReturnUrlPath, getSummaryButtonTooltip } from './lib';
import SummaryEmptyState from './SummaryEmptyState';
import SummaryLoadingState from './SummaryLoadingState';
import SummarySection from './SummarySection';

function SummaryErrorState({
  onSummarizeFiles,
  disabled,
  tooltipText,
}: {
  onSummarizeFiles: () => void;
  disabled?: boolean;
  tooltipText?: string;
}) {
  return (
    <ErrorContainer>
      <span>Summary failed to process. Click below to try again.</span>
      <div style={{ width: 148 }}>
        <Tooltip text={tooltipText || ''} disabled={!disabled || !tooltipText}>
          <div>
            <AIButton onClick={onSummarizeFiles} fullWidth disabled={disabled}>
              Try Again
            </AIButton>
          </div>
        </Tooltip>
      </div>
    </ErrorContainer>
  );
}

type Props = RenderingErrorBoundaryChildProps & {
  review: SummarizableReview;
  pendingActions?: Array<'summarize' | 'compare'>;
  isCompared?: boolean;
  onCompareSummaries?: () => void;
};

export default function SummaryTab({
  review,
  pendingActions = [],
  isErrored = false,
  isCompared = false,
  onCompareSummaries,
  resetErrorBoundary,
}: Props) {
  // Check if AI summary and comparison buttons should be disabled
  const { profile } = useProfile();
  const { episode } = useEpisode();
  const { locationEpisode } = useSelectedLocationEpisode();
  const location = useLocation();
  const { startTracking, isTracking } = useTrackAIJob();

  const canCreateSummary = profile?.has(PERMISSIONS.aiJobSummaryCreate, { episode, locationEpisode }) ?? false;
  const canCreateComparison = profile?.has(PERMISSIONS.aiJobComparisonCreate, { episode, locationEpisode }) ?? false;
  const isLocationEpisodeDischarged = locationEpisode?.discharged ?? false;

  const isAuthorization = review.type === ReviewType.AUTHORIZATION;
  const { summaryJob, summary } = review;
  const summaryJobInProgress = summaryJob && summaryJob.isProcessing && !summary;
  const filesCount = review?.attachmentCount ?? review?.attachments?.length ?? 0;

  const allAttachmentsFailedProcessing = review.attachments.every(
    (attachment) => attachment.extractDataAIJob?.isErrored
  );

  const shouldDisableSummaryButton = allAttachmentsFailedProcessing || !canCreateSummary || isLocationEpisodeDischarged;
  const shouldDisableComparisonButton =
    (!isCompared && (!canCreateComparison || isLocationEpisodeDischarged)) ||
    (isAuthorization && !review.previousAuthorizationReview?.summary);

  const summaryButtonTooltip = getSummaryButtonTooltip({
    reviewType: review.type,
    attachments: review.attachments,
    allAttachmentsFailedProcessing,
  });

  const [isCreatingJob, setIsCreatingJob] = useState(false);

  const shouldShowLoadingState = isCreatingJob || summaryJobInProgress;

  const [showLoadingState, setShowLoadingState] = useState(shouldShowLoadingState);

  const queryKey = (isAuthorization ? authorizationQueryKeys : referralQueryKeys).show({ id: review.id });
  const patientName = (() => {
    if (episode?.patient) return episode.patient.name;
    if (review.type === ReviewType.REFERRAL) return (review as Referral).episode.patient.name;
    return 'patient';
  })();

  const handleCollapsePromptExplainer = useCallback(() => {
    if (!summaryJob) return;
    updateReviewCacheWithAIJob(
      queryKey,
      new AIJob({
        ...summaryJob,
        hasCollapsedPromptExplainer: true,
      } as AIJobOptions)
    );
  }, [summaryJob, queryKey]);

  const handleSummarizeFiles = useCallback(async () => {
    resetErrorBoundary?.();
    setIsCreatingJob(true);
    try {
      const newJob = await createAIJob({
        type: 'summary',
        reviewId: review?.id,
      });
      updateReviewCacheWithAIJob(queryKey, newJob);
    } catch {
      setIsCreatingJob(false);
    }
  }, [review.id, resetErrorBoundary, queryKey]);

  // Check if we need to track on page load and on a job update
  useEffect(() => {
    if (summaryJobInProgress && !isTracking(summaryJob.id) && location.pathname.endsWith('/summary')) {
      startTracking({
        jobId: summaryJob.id,
        jobType: isAuthorization ? AIJobNotificationType.AUTHORIZATION_SUMMARY : AIJobNotificationType.REFERRAL_SUMMARY,
        jobbableId: review.id,
        patientName: patientName,
        returnUrl: getReturnUrlPath(location.pathname),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryJobInProgress, location.pathname]);

  // Handle pending actions from other tabs
  useEffect(() => {
    const summarizeIndex = pendingActions.findIndex((action) => action === 'summarize');
    if (summarizeIndex !== -1 && !summaryJob && !allAttachmentsFailedProcessing && !isCreatingJob) {
      pendingActions.splice(summarizeIndex, 1);
      handleSummarizeFiles();
    }
  }, [handleSummarizeFiles, pendingActions, summaryJob, filesCount, isCreatingJob, allAttachmentsFailedProcessing]);

  useEffect(() => {
    if (shouldShowLoadingState) {
      setShowLoadingState(true);
    }
  }, [shouldShowLoadingState]);

  useEffect(() => {
    if (isCreatingJob && summaryJobInProgress) setIsCreatingJob(false);
  }, [summaryJobInProgress, isCreatingJob]);

  const handleLoadingFinished = useCallback(() => {
    setShowLoadingState(false);
  }, []);

  // Error state
  if (isErrored || summaryJob?.isErrored) {
    return (
      <SummaryErrorState
        onSummarizeFiles={handleSummarizeFiles}
        disabled={shouldDisableSummaryButton}
        tooltipText={summaryButtonTooltip}
      />
    );
  }

  if (showLoadingState) {
    return <SummaryLoadingState review={review} onLoadingFinished={handleLoadingFinished} />;
  }

  if (!summary) {
    return (
      <SummaryEmptyState
        filesCount={filesCount}
        summaryType={isAuthorization ? ReviewType.AUTHORIZATION : ReviewType.REFERRAL}
        onSummarizeFiles={handleSummarizeFiles}
        disableSummaryButton={shouldDisableSummaryButton}
        tooltipText={summaryButtonTooltip}
      />
    );
  }

  const erroredAttachments = review.attachments.filter(
    (attachment) => !summaryJob?.attachmentIds.includes(attachment.id)
  );

  return (
    <SummarizedContainer>
      {erroredAttachments.length > 0 && (
        <ErroredAttachmentsContainer>
          <ErrorWarningIcon />
          <ErroredAttachmentsText>
            <span>The following file(s) failed to process and are not included in the summary:</span>
            <ErroredAttachmentsList>
              {erroredAttachments.map((attachment) => (
                <li key={attachment.id}>{attachment.filename}</li>
              ))}
            </ErroredAttachmentsList>
          </ErroredAttachmentsText>
        </ErroredAttachmentsContainer>
      )}
      <MetadataSpan>
        Summary created by {summaryJob?.createdBy?.fullName || 'Unknown'}
        {summaryJob?.createdByGroup?.name && ` at ${summaryJob.createdByGroup.name}`} on{' '}
        {summaryJob?.createdAt ? simpleDate(summaryJob.createdAt) : 'Unknown date'}
      </MetadataSpan>
      <ActionContainer>
        {profile.hasFlag(Flags.ComparisonForAiSummary) && isAuthorization && !!review.previousAuthorizationReview && (
          <AIButton data-cy='CompareSummaries' disabled={shouldDisableComparisonButton} onClick={onCompareSummaries}>
            {isCompared ? 'View Comparison' : 'Compare'} to{' '}
            {simpleDate(review.previousAuthorizationReview.approvedThroughDate)}
          </AIButton>
        )}
        <DownloadAIExportButton artifactId={summary.id} artifactType='summary' />
      </ActionContainer>
      <Explainer
        title="How Olio's Summary Works"
        aiPrompt={summary.aiPrompt ?? undefined}
        hasBeenCollapsed={summaryJob?.hasCollapsedPromptExplainer ?? undefined}
        onCollapseSuccess={handleCollapsePromptExplainer}
      />
      <SourcesContainer>
        <SourcesTitle>Sources ({summary.data.sources.length})</SourcesTitle>
        <CitationsContainer>
          {summary.data.sources.map((attachment) => (
            <SourceChip key={attachment.attachmentId} attachment={attachment} />
          ))}
        </CitationsContainer>
      </SourcesContainer>
      <ContentContainer>
        {summary.data.sections.map(({ title, attachmentIds, content }, idx) => (
          <SummarySection
            key={`summary-section-${idx}`}
            title={title}
            attachments={attachmentIds.flatMap(
              (x) => summary.data.sources.find(({ attachmentId }) => attachmentId == x) || []
            )}
            content={content}
          />
        ))}
      </ContentContainer>
    </SummarizedContainer>
  );
}

const MetadataSpan = styled.span`
  font-style: italic;
  font-size: 12px;
  line-height: 12px;
  letter-spacing: 0.23px;
`;

const SummarizedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const ActionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const SourcesContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
`;

const SourcesTitle = styled.h2`
  font-weight: 700;
  font-size: 14px;
  line-height: 12px;
  letter-spacing: 0.2px;
`;

const CitationsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const ErroredAttachmentsContainer = styled.div`
  border: 1px solid ${colors.accentRed25};
  background-color: ${colors.accentRed10};
  border-radius: 4px;
  padding: 16px;
  font-size: 12px;

  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
`;

const ErroredAttachmentsText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ErroredAttachmentsList = styled.ul`
  margin: 0;
  padding-left: 20px;

  li {
    margin-bottom: 4px;
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  height: 100%;

  font-weight: 400;
  font-size: 16px;
  line-height: 16px;
  letter-spacing: 2%;
  text-align: center;
`;
